// «La tua posta»: tutto quello che si sono detti, dal più recente, in ordine di data.
// Qui non si segna niente come visto e niente sparisce: le «Novità» sono la porta di
// oggi, questa è la cassetta che resta. I vocali si riascoltano sempre, senza autoplay.
import { api, ApiError } from '../lib/api.js';
import { el, fmtDate, quandoRelativo } from '../lib/ui.js';
import { filoUI, soloEmoji, type Novita, type Risposta, type RispostaOspitePosta, type VocePosta } from '../lib/filo-ui.js';

type UI = {
  screen: (...nodes: Array<Node | string | null | undefined>) => void;
  testata: (title: string) => HTMLElement;
  speak: (text: string, opts?: { force?: boolean }) => Promise<void>;
  caro: string;
  apriRisposta: (memoryId: number, area: HTMLElement, opts: { onDone: (r: Risposta) => void; onCancel: () => void }) => void;
  esitoRisposta: (area: HTMLElement, r: Risposta, indietro: () => void) => void;
  /** Le novità di oggi, in cima. Senza, la posta mostra tutto (anche le cose nuove) come prima. */
  daVedere?: () => Promise<{ element: HTMLElement; chiavi: Set<string> } | null>;
};

type Pagina = { items: VocePosta[]; offset: number; limit: number; hasMore: boolean; totale: number };

const PER_PAGINA = 20;

/** «Settembre 2026» dalla chiave «2026-09». */
function titoloMese(chiave: string): string {
  const testo = fmtDate(chiave);
  return testo.charAt(0).toUpperCase() + testo.slice(1);
}

async function caricaPagina(offset: number): Promise<Pagina> {
  try {
    return await api.get<Pagina>(`/api/posta?offset=${offset}&limit=${PER_PAGINA}`);
  } catch (err) {
    // App più nuova del server (aggiornamento a metà): meglio la cassetta vuota di una schermata d'errore.
    if (err instanceof ApiError && err.status === 404) return { items: [], offset, limit: PER_PAGINA, hasMore: false, totale: 0 };
    throw err;
  }
}

export async function renderPosta(ui: UI): Promise<void> {
  const { iconaNovita, fraseNovita, azioneNovita } = filoUI({ caro: () => ui.caro, speak: ui.speak });
  const lista = el('div', { class: 'pila posta-lista' });
  const piede = el('div', { class: 'posta-piede' });
  let meseMostrato = '';
  let offset = 0;

  function citazione(v: VocePosta): HTMLElement | null {
    if (v.type === 'reazione' && v.kind === 'text' && v.text && !soloEmoji(v.text)) return el('p', { class: 'citazione' }, `“${v.text}”`);
    if (v.type === 'richiesta' && v.text) return el('p', { class: 'citazione' }, `“${v.text}”`);
    return null;
  }

  /** Una sua risposta a un parente: la frase, le sue parole, e se lui l'ha letta. */
  function cartaRispostaOspite(v: RispostaOspitePosta): HTMLElement {
    const nome = v.guestName;
    const stato = v.stato === 'vista' ? `${nome} l’ha letta ✓`
      : v.stato === 'email' ? 'Mandata anche per email ✓' : `${nome} non l’ha ancora letta`;
    return el('div', { class: 'carta carta-vincenzo novita-card novita-vista posta-risposta-ospite' },
      el('p', { class: 'etichetta' }, `💌 ${quandoRelativo(v.when)}`),
      el('p', { class: 'domanda-testo' }, `Hai risposto a ${nome}${v.title ? ` sotto «${v.title}»` : ''}`),
      el('p', { class: 'citazione' }, `“${v.text}”`),
      el('p', { class: 'quando' }, stato),
      el('div', { class: 'azioni' },
        el('button', { class: 'btn-secondario', click: () => { location.hash = `#storia/${v.memoryId}`; } }, '📖  Vai al racconto')),
    );
  }

  function cartaPosta(v: VocePosta): HTMLElement {
    return v.type === 'risposta_ospite' ? cartaRispostaOspite(v) : cartaFilo(v);
  }

  function cartaFilo(v: Novita & { nuovo: boolean }): HTMLElement {
    const area = el('div', { class: 'azioni' });
    const carta = el('div', { class: `carta carta-vincenzo novita-card${v.nuovo ? '' : ' novita-vista'}` },
      el('p', { class: 'etichetta' },
        `${iconaNovita(v)} ${quandoRelativo(v.when)}`,
        v.nuovo ? el('span', { class: 'posta-nuovo' }, 'nuovo') : null),
      el('p', { class: 'domanda-testo' }, fraseNovita(v)),
      citazione(v),
      area,
    );

    function mostraAzioni(): void {
      if (v.type === 'domanda') {
        const racconto = v.memoryId;
        area.replaceChildren(
          azioneNovita(v, 'btn-secondario') ?? '',
          racconto
            ? el('button', { class: 'btn-secondario', click: () => { location.hash = `#storia/${racconto}`; } }, '📖  Vai al racconto che hai fatto')
            : el('button', { class: 'btn-primario', click: () => { location.hash = `#registra?mode=dialogo&q=${v.questionId}`; } }, '🎙️  Rispondi raccontando'),
        );
        return;
      }
      if (v.type === 'voce') {
        area.replaceChildren(el('button', { class: 'btn-secondario', click: () => { location.hash = `#storia/${v.memoryId}`; } },
          v.kind === 'saluto' ? '💌  Rileggilo' : '🎧  Ascoltalo'));
        return;
      }
      if (v.type === 'richiesta') {
        area.replaceChildren(
          azioneNovita(v, 'btn-secondario') ?? '',
          el('button', { class: 'btn-secondario', click: () => { location.hash = `#chiedi?conversazione=${v.requestId}`; } }, '💬  Vai alla conversazione'),
        );
        return;
      }
      area.replaceChildren(
        azioneNovita(v, 'btn-secondario') ?? '',
        el('button', { class: 'btn-primario', click: () => ui.apriRisposta(v.memoryId, area, { onDone: (r) => ui.esitoRisposta(area, r, mostraAzioni), onCancel: mostraAzioni }) }, `💬  Rispondi a ${ui.caro}`),
        el('button', { class: 'btn-secondario', click: () => { location.hash = `#storia/${v.memoryId}`; } }, '📖  Vai alla storia'),
      );
    }
    mostraAzioni();
    return carta;
  }

  /** Accoda una pagina in fondo, aprendo un mese nuovo quando serve. Torna il primo pezzo aggiunto. */
  function accoda(items: VocePosta[]): HTMLElement | null {
    let primo: HTMLElement | null = null;
    // Quello che è già in «Da vedere» non si ripete sotto: scende qui quando l'ha visto. Solo
    // quello mostrato davvero in cima: il resto, anche se «nuovo», resta qui e non sparisce.
    for (const v of items.filter((i) => !inCima.has(`${i.type}:${i.id}`))) {
      const mese = v.when.slice(0, 7);
      if (mese !== meseMostrato) {
        meseMostrato = mese;
        const titolo = el('h2', { class: 'sezione' }, titoloMese(mese));
        lista.append(titolo);
        primo ??= titolo;
      }
      const carta = cartaPosta(v);
      lista.append(carta);
      primo ??= carta;
    }
    return primo;
  }

  function mostraPiede(hasMore: boolean): void {
    if (!hasMore) {
      piede.replaceChildren(lista.childElementCount > 0
        ? el('p', { class: 'data-oggi centrato' }, 'Questo è tutto quello che vi siete detti fin qui.')
        : el('div', { class: 'carta centrato' },
            el('p', { class: 'domanda-testo' }, 'Qui non c’è ancora niente.'),
            el('p', {}, `Quando ${ui.caro} ti lascerà un messaggio, un cuore o una domanda, lo ritroverai sempre qui.`),
          ));
      return;
    }
    piede.replaceChildren(el('button', {
      class: 'btn-secondario',
      click: async () => {
        const pagina = await caricaPagina(offset).catch((err: unknown) => {
          // Ha cambiato schermata mentre caricava: la lettura è annullata, non è un errore da mostrarle.
          if (err instanceof DOMException && err.name === 'AbortError') return null;
          throw err;
        });
        if (!pagina) return;
        offset += pagina.items.length;
        const primo = accoda(pagina.items);
        mostraPiede(pagina.hasMore && pagina.items.length > 0);
        // Il fuoco segue il primo pezzo nuovo: la lettura riprende da lì, non dall'inizio.
        if (primo) { primo.tabIndex = -1; primo.focus(); }
      },
    }, 'Mostra anche i mesi precedenti'));
  }

  // Le novità in cima; se non arrivano (rete, server vecchio) la posta resta intera, come prima.
  const daVedere = ui.daVedere ? await ui.daVedere().catch(() => null) : null;
  const inCima = daVedere?.chiavi ?? new Set<string>();
  const prima = await caricaPagina(0);
  offset = prima.items.length;
  accoda(prima.items);
  mostraPiede(prima.hasMore && prima.items.length > 0);
  ui.screen(
    ui.testata('La tua posta'),
    el('p', { class: 'data-oggi' }, daVedere
      ? 'In cima le cose nuove. Sotto c’è tutto il resto, dal più recente: niente sparisce.'
      : 'Qui c’è tutto, dal più recente. Niente sparisce: puoi riascoltare quando vuoi.'),
    daVedere?.element ?? null,
    daVedere ? el('h2', { class: 'sezione' }, 'Tutto quello che vi siete detti') : null,
    lista,
    piede,
  );
}
